import { useState } from 'react';

import { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import {
  FooterComponent,
  HeaderComponent,
} from '@/components/BasePages/StandardPage';
import EcommOptionsList from '@/components/EcommOptionsList/EcommOptionsList';
import { Carousel } from '@/components/ProductPageBase/Carousel/Carousel';
import SunrunShopProductPageBase from '@/components/ProductPageBase/ProductPageBase';
import { Section } from '@/components/Section/Section';
import AddToCartButton from '@/components/SunrunShop/AddToCartButton/AddToCartButton';
import BuyButton from '@/components/SunrunShop/BuyButton/BuyButton';
import type { GlobalFooterContent, GlobalNavContent } from '@/contentful';
import type { SunrunShopProductPageEnhancedCF } from '@/contentful/api/types';
import type { userLocationInterface } from '@/hooks/geo';
import { formatUSD } from '@/utils/currency';

import useStyles from './SunrunShopProductPage.styles';

interface Props extends userLocationInterface {
  content: SunrunShopProductPageEnhancedCF;
}

export default function SunrunShopProductPage({
  content,
  userLocation,
  setUserLocation,
}: Props) {
  const { classes } = useStyles();

  const { fields } = content;
  const { topSections, product, bottomSections, header, footer } = fields;
  const variants = product?.variants?.nodes;

  const [selectedVariantId, setSelectedVariantId] = useState(variants?.[0].id);

  const title = product?.title || '';
  const description = product?.description || '';
  const fieldImages =
    fields?.product?.images?.nodes?.map((node) => node.url) || [];
  const subheading = fields.subheading;
  const disclaimer = fields.disclaimer;
  const buyNowButtonLabel = fields?.buyNowButtonLabel;
  const addToCartButtonLabel = fields?.addToCartButtonLabel;

  const [images, setImages] = useState<string[]>(fieldImages);
  const [hoveredImage, setHoveredImage] = useState<string>();

  const selectedVariant = variants?.find((v) => v.id === selectedVariantId);

  const moveElementToFront = (arr: string[], elementToMove: string) => {
    const index = arr.indexOf(elementToMove);
    if (index >= 0) {
      const [tempElement] = arr.splice(index, 1);
      arr.unshift(tempElement);
    }
    return arr;
  };

  const onSelect = (variantId: string) => {
    setSelectedVariantId(variantId);

    const newVariant = variants?.find((v) => v.id === variantId);
    const urlToMove = newVariant?.image.url;

    if (urlToMove) {
      setImages(moveElementToFront(images, urlToMove));
    }
  };

  const onHover = (hoveredId: string, isEntering: boolean) => {
    if (isEntering) {
      const hoveredVariant = variants?.find((v) => v.id === hoveredId);
      setHoveredImage(hoveredVariant?.image.url);
    } else {
      setHoveredImage(undefined);
    }
  };

  return (
    <>
      <HeaderComponent
        header={header as unknown as GlobalNavContent}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
      />
      {topSections?.map((topSection, index) => (
        <Section
          key={topSection.sys.id}
          section={topSection}
          userLocation={userLocation}
          sectionPosition={index}
        />
      ))}
      <SunrunShopProductPageBase
        heading={title}
        subheading={subheading || ''}
        imageContent={
          <Carousel selectedImageOverride={hoveredImage} images={images} />
        }
        description={description}
        optionCardLists={[
          variants && variants.length > 1 && (
            <EcommOptionsList
              key='ecomm-options-list'
              heading='Options'
              label='Options'
              name='options'
              items={variants?.map(({ id, title }) => ({
                id,
                label: title,
              }))}
              selectedId={selectedVariantId}
              onSelect={onSelect}
              onHover={onHover}
              optionsContainerClassName={classes.optionsContainer}
              cardContentClassName={classes.cardContent}
            />
          ),
        ]}
        totalPrice={
          selectedVariant && formatUSD(selectedVariant.price.amount, 2)
        }
        checkoutButtons={[
          ...(buyNowButtonLabel
            ? [
                <BuyButton
                  key='buy-now'
                  variantId={selectedVariantId}
                  className={classes.buyNowButton}
                  label={buyNowButtonLabel}
                />,
              ]
            : []),
          ...(addToCartButtonLabel
            ? [
                <AddToCartButton
                  key='add-to-cart'
                  variantId={selectedVariantId}
                  className={classes.buyNowButton}
                  label={addToCartButtonLabel}
                  variant={
                    buyNowButtonLabel
                      ? ButtonVariant.SECONDARY
                      : ButtonVariant.PRIMARY
                  }
                />,
              ]
            : []),
        ]}
        disclaimer={disclaimer}
      />
      {bottomSections?.map((bottomSection, index) => (
        <Section
          key={bottomSection.sys.id}
          section={bottomSection}
          userLocation={userLocation}
          sectionPosition={index}
        />
      ))}
      <FooterComponent
        footer={footer as unknown as GlobalFooterContent}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
      />
    </>
  );
}
